import { useState } from "react";
import { ChoiceSelect, FieldRow } from "./parts";

/** A column the field offers. */
export interface CrmColumnChoice {
  /** "board_id:column_id", as triggers, waits and moves name a column. */
  key: string;
  boardId: number;
  /** Its visualization's name: the CRM page's word for a board. */
  boardName: string;
  name: string;
  /** Why it can't be picked here; shown under it, and it is off. */
  refusal?: string | null;
}

/**
 * One CRM column, picked on one row: the visualization, then one of its
 * columns. Picking a visualization with only one column that can be picked
 * picks it. Picking one with more clears the column (onChange gets null and
 * the visualization) until one is picked. The visualizations come in the
 * order of their columns. boardId: the visualization picked with no column
 * yet, for a caller that keeps it. labels false: no labels over the
 * pickers, which then say what they are (a menu's own row). fill: each
 * picker takes half the row, as the dialogs' fields fill them.
 */
export default function CrmColumnField({
  columns,
  value,
  boardId = null,
  onChange,
  labels = true,
  fill = true,
}: {
  columns: CrmColumnChoice[];
  value: string | null | undefined;
  boardId?: number | null;
  onChange: (key: string | null, boardId: number) => void;
  labels?: boolean;
  fill?: boolean;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  if (!columns.length)
    return (
      <p className="mt-3 text-sm text-red-600">
        Make a visualization first, on the CRM page.
      </p>
    );

  const current = columns.find((c) => c.key === value) ?? null;
  const board = current?.boardId ?? boardId ?? picked;
  const boards = columns.filter(
    (c, i) => columns.findIndex((o) => o.boardId === c.boardId) === i
  );
  const own = columns.filter((c) => c.boardId === board);

  const pickBoard = (id: number) => {
    if (id === board) return;
    const open = columns.filter((c) => c.boardId === id && !c.refusal);
    setPicked(id);
    onChange(open.length === 1 ? open[0].key : null, id);
  };
  const pickColumn = (key: string) => {
    const column = columns.find((c) => c.key === key);
    if (column && key !== value) onChange(key, column.boardId);
  };

  const width = fill ? "w-full" : "min-w-[14rem] max-w-xs";
  const visualization = (
    <ChoiceSelect<number>
      label="Visualization"
      value={board ?? undefined}
      options={boards.map((c) => ({ value: c.boardId, label: c.boardName }))}
      placeholder={labels ? "Pick a visualization" : "Visualization"}
      onChange={pickBoard}
      className={width}
    />
  );
  const column = (
    <ChoiceSelect<string>
      label="Column"
      value={current?.key}
      options={own.map((c) => ({
        value: c.key,
        label: c.name,
        hint: c.refusal ?? undefined,
        isDisabled: !!c.refusal,
      }))}
      placeholder={
        !labels
          ? "Column"
          : board === null
          ? "Pick a visualization first"
          : "Pick a column"
      }
      disabled={board === null}
      onChange={pickColumn}
      className={width}
    />
  );

  const row = fill ? "grid grid-cols-2 gap-x-3" : "flex flex-wrap gap-x-4";
  if (!labels)
    return (
      <div className={fill ? "grid grid-cols-2 gap-2" : "flex flex-wrap gap-2"}>
        {visualization}
        {column}
      </div>
    );
  return (
    <div className={row}>
      <FieldRow label="Visualization">{visualization}</FieldRow>
      <FieldRow label="Column">{column}</FieldRow>
    </div>
  );
}

/** A CRM column choice from the trigger catalog's (CatalogChoice), "Board › Column" read apart when it says less. */
export function catalogColumnChoice(choice: {
  value: string;
  label: string;
  board_id?: number | null;
  board_name?: string | null;
  column_name?: string | null;
}): CrmColumnChoice {
  const at = choice.label.indexOf(" › ");
  return {
    key: choice.value,
    boardId: choice.board_id ?? Number(choice.value.split(":")[0]),
    boardName:
      choice.board_name ?? (at < 0 ? choice.label : choice.label.slice(0, at)),
    name:
      choice.column_name ??
      (at < 0 ? choice.label : choice.label.slice(at + 3)),
  };
}
